import React, { useState } from 'react';
import {
  Star,
  ExternalLink,
  Sparkles,
  Scale,
  Edit3,
  Trash2,
  RefreshCw,
  BedDouble,
  Bath,
  Ruler,
  CalendarDays,
  Dog,
  Sofa,
  Car,
  Eye,
  MapPin,
  UserCircle,
  ImageIcon,
  TrendingDown,
} from '../../icons';
import Sheet from '../ui/Sheet';
import Button from '../ui/Button';
import StatTile from '../ui/StatTile';
import PlatformBadge from '../ui/PlatformBadge';
import PriceHistoryChart, { normalizeHistory } from '../ui/PriceHistoryChart';
import { CardSection } from '../ui/Card';
import { cn } from '../ui/cn';
import { formatDate } from '../../utils/dateUtils';
import { useIsDesktop } from '../../hooks/useMediaQuery';
import { formatPropertyReference } from '../../utils/propertyPlatforms';
import { formatEuro, getPriceDrop, DeleteConfirmDialog } from './PropertyCard';

/**
 * Scraped notes look like "Title: …\nDescription: …". Split them so the detail
 * view can show a Description card and a Notes card without duplication.
 */
export const splitNotes = (notes = '') => {
  const text = String(notes || '').trim();
  if (!text) return { description: '', notes: '' };
  const descIdx = text.indexOf('Description:');
  if (descIdx === -1) {
    // Old format: "Title: X\n<description>"
    const m = text.match(/^Title:[^\n]*\n([\s\S]*)$/);
    if (m) return { description: m[1].trim(), notes: '' };
    return { description: '', notes: text };
  }
  const before = text.slice(0, descIdx).replace(/^Title:[^\n]*\n?/, '').trim();
  let after = text.slice(descIdx + 'Description:'.length).trim();
  let extra = '';
  const upd = after.indexOf('Scraped update:');
  if (upd !== -1) {
    extra = after.slice(upd).trim();
    after = after.slice(0, upd).trim();
  }
  const rest = [before, extra].filter(Boolean).join('\n\n');
  return { description: after, notes: rest };
};

const Row = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 text-sm text-secondary">
    <Icon className="w-4 h-4 text-muted flex-shrink-0" />
    <span className="truncate">{children}</span>
  </div>
);

/**
 * Full-screen property detail. Mobile: full-screen page. Desktop: ~92% centered
 * two-column dialog. Same handlers as PropertyCard.
 */
const PropertyDetail = ({
  property,
  isOpen,
  onClose,
  onEdit,
  onDelete,
  onToggleFavorite,
  onAnalyze,
  onCompare,
  onRefetch,
  isRefetching,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const isDesktop = useIsDesktop();

  if (!property) return null;

  const sold = !!property.sold;
  const drop = getPriceDrop(property);
  const { description, notes } = splitNotes(property.notes);
  const history = (property.history || []).slice().sort(
    (a, b) => (b.snapshotTimestamp?.toDate?.() || 0) - (a.snapshotTimestamp?.toDate?.() || 0)
  );
  const chartPoints = normalizeHistory(property.history);
  const showImage = property.imageURL && !imgFailed;
  const reference = property.platform && property.propertyId ? formatPropertyReference(property) : null;
  const notNA = (v) => v && v !== 'N/A';

  const headerRight = (
    <div className="flex items-center gap-1 pt-1">
      <button
        type="button"
        onClick={() => onToggleFavorite(property.id)}
        aria-label={property.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
        aria-pressed={!!property.isFavorite}
        className={cn(
          'w-11 h-11 flex items-center justify-center rounded-xl hover:bg-white/5',
          property.isFavorite ? 'text-amber' : 'text-secondary hover:text-amber'
        )}
      >
        <Star className="w-5 h-5" filled={property.isFavorite} />
      </button>
      {property.url && (
        <a
          href={property.url}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden sm:inline-flex items-center gap-1.5 h-11 px-3 rounded-xl text-sm font-medium text-accent hover:bg-accent-soft"
        >
          Open listing <ExternalLink className="w-4 h-4" />
        </a>
      )}
    </div>
  );

  const actions = (
    <div className="grid grid-cols-3 gap-2">
      <Button variant="primary" onClick={() => onAnalyze(property)} disabled={sold}>
        <Sparkles className="w-4 h-4" /> Analyze
      </Button>
      <Button variant="secondary" onClick={() => onCompare(property)} disabled={sold}>
        <Scale className="w-4 h-4" /> Compare
      </Button>
      <Button variant="secondary" onClick={() => onRefetch(property.id)} disabled={isRefetching} loading={isRefetching}>
        {!isRefetching && <RefreshCw className="w-4 h-4" />} {isRefetching ? 'Fetching' : 'Refetch'}
      </Button>
    </div>
  );

  const priceBlock = (
    <div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={cn('text-[30px] lg:text-[34px] font-bold tabular tracking-tight leading-none', sold ? 'text-secondary' : 'text-accent')}>
          {sold ? 'Sold' : formatEuro(property.lastKnownPrice)}
        </span>
        {drop && !sold && (
          <>
            <span className="text-sm text-muted line-through tabular">was {formatEuro(drop.startPrice)}</span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-danger text-white text-xs font-semibold">
              <TrendingDown className="w-3.5 h-3.5" /> -{drop.dropPct}%
            </span>
          </>
        )}
        {property.startPrice && !drop && String(property.startPrice) !== String(property.lastKnownPrice) && !sold && (
          <span className="text-sm text-muted tabular">initial {formatEuro(property.startPrice)}</span>
        )}
      </div>
      <div className="mt-1.5 flex items-center gap-1.5 text-xs text-secondary flex-wrap">
        {property.forSale && <span className="px-1.5 py-0.5 rounded bg-page border border-line">For sale</span>}
        {property.forRent && <span className="px-1.5 py-0.5 rounded bg-page border border-line">For rent</span>}
        {sold && <span className="px-1.5 py-0.5 rounded bg-danger-soft text-danger border border-danger/30 font-semibold">SOLD</span>}
        {property.lastReportedUpdateDate && <span>Site update {property.lastReportedUpdateDate}</span>}
        {property.publishedDate && <span>· Published {property.publishedDate}</span>}
      </div>
    </div>
  );

  const media = (
    <div className="relative rounded-card overflow-hidden bg-card-2 aspect-[16/9]">
      {showImage ? (
        <img src={property.imageURL} alt={property.title || 'Property'} referrerPolicy="no-referrer" className="w-full h-full object-cover" onError={() => setImgFailed(true)} />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-[#213043] to-[#152131] flex items-center justify-center text-muted">
          <ImageIcon className="w-12 h-12" />
        </div>
      )}
      <div className="absolute top-3 left-3 flex items-center gap-1.5">
        <PlatformBadge platform={property.platform} size="md" />
        {property.isDistressed && <span className="px-2 py-1 rounded-md bg-amber text-[#2A1C00] text-xs font-semibold">Distressed</span>}
      </div>
    </div>
  );

  const statTiles = (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      {property.bedrooms && <StatTile icon={BedDouble} label="Beds" value={property.bedrooms} />}
      {property.bathrooms && <StatTile icon={Bath} label="Baths" value={property.bathrooms} />}
      {property.area && <StatTile icon={Ruler} label="Area" value={`${property.area} m²`} />}
      {notNA(property.constructionYear) && <StatTile icon={CalendarDays} label="Built" value={property.constructionYear} />}
    </div>
  );

  const extras = [
    notNA(property.pets) && { icon: Dog, text: `Pets: ${property.pets}` },
    notNA(property.furnishing) && { icon: Sofa, text: property.furnishing },
    notNA(property.parking) && { icon: Car, text: `Parking: ${property.parking}` },
    notNA(property.onlineViewing) && { icon: Eye, text: `Online viewing: ${property.onlineViewing}` },
    property.cityId && { icon: MapPin, text: `City ID: ${property.cityId}` },
  ].filter(Boolean);

  const descriptionCard = (description || extras.length > 0 || (property.latitude && property.longitude)) && (
    <CardSection title="Description">
      {description && <p className="text-sm text-secondary whitespace-pre-wrap leading-relaxed">{description}</p>}
      {(extras.length > 0 || (property.latitude && property.longitude)) && (
        <div className={cn('grid grid-cols-1 sm:grid-cols-2 gap-2', description && 'mt-3 pt-3 border-t border-line')}>
          {extras.map((e, i) => (
            <Row key={i} icon={e.icon}>
              {e.text}
            </Row>
          ))}
          {property.latitude && property.longitude && (
            <a
              href={`https://maps.google.com/?q=${property.latitude},${property.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-accent hover:underline"
            >
              <MapPin className="w-4 h-4" /> View on map
            </a>
          )}
        </div>
      )}
    </CardSection>
  );

  const landlordCard = property.landlordName && (
    <CardSection title="Landlord">
      <div className="flex items-center gap-3">
        <span className="w-9 h-9 rounded-full bg-page border border-line flex items-center justify-center text-secondary">
          <UserCircle className="w-5 h-5" />
        </span>
        <div className="min-w-0">
          <div className="text-sm font-medium text-primary truncate">{property.landlordName}</div>
          <div className="text-xs text-secondary">
            {property.landlordJoined && <span>Joined {property.landlordJoined}</span>}
            {property.ownerAdCount && <span>{property.landlordJoined ? ' · ' : ''}{property.ownerAdCount} ads</span>}
          </div>
        </div>
      </div>
    </CardSection>
  );

  const historyCard = (
    <CardSection title={`Price history (${history.length})`}>
      {chartPoints.length >= 2 ? (
        <PriceHistoryChart history={property.history} className="mb-3" />
      ) : (
        <p className="text-xs text-muted mb-3">The chart appears once two or more price snapshots exist.</p>
      )}
      {history.length > 0 ? (
        <div className="overflow-x-auto -mx-4 px-4">
          <table className="min-w-full text-xs text-left">
            <thead className="text-muted uppercase tracking-wide">
              <tr>
                <th className="py-1.5 pr-3 font-medium">Date</th>
                <th className="py-1.5 pr-3 font-medium">Price</th>
                <th className="py-1.5 pr-3 font-medium">Site publish</th>
                <th className="py-1.5 font-medium">Site update</th>
              </tr>
            </thead>
            <tbody>
              {history.map((entry, index) => (
                <tr key={index} className="border-t border-line text-secondary">
                  <td className="py-1.5 pr-3 whitespace-nowrap">{formatDate(entry.snapshotTimestamp)}</td>
                  <td className="py-1.5 pr-3 font-semibold text-primary tabular whitespace-nowrap">{formatEuro(entry.price)}</td>
                  <td className="py-1.5 pr-3 whitespace-nowrap">{entry.reportedPublishedDate || '—'}</td>
                  <td className="py-1.5 whitespace-nowrap">{entry.reportedUpdateDate || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-xs text-muted">No snapshots recorded yet.</p>
      )}
    </CardSection>
  );

  const notesCard = (
    <CardSection
      title="Notes"
      action={
        !sold && (
          <button type="button" onClick={() => onEdit(property)} className="text-xs font-medium text-accent hover:underline">
            Edit notes
          </button>
        )
      }
    >
      {notes ? (
        <p className="text-sm text-secondary whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto">{notes}</p>
      ) : (
        <p className="text-xs text-muted">No notes yet.</p>
      )}
    </CardSection>
  );

  const footerRow = (
    <div className="flex items-center justify-between gap-3">
      <Button variant="secondary" onClick={() => onEdit(property)} disabled={sold}>
        <Edit3 className="w-4 h-4" /> Edit
      </Button>
      <Button variant="danger" onClick={() => setShowDeleteConfirm(true)}>
        <Trash2 className="w-4 h-4" /> Delete
      </Button>
    </div>
  );

  return (
    <>
      <Sheet
        isOpen={isOpen}
        onClose={onClose}
        mode="page"
        title={property.title || (property.url || '').replace(/^https?:\/\/(www\.)?/, '')}
        subtitle={reference || undefined}
        headerRight={headerRight}
        aria-label="Property details"
        bodyClassName="lg:py-6"
      >
        {isDesktop ? (
          /* Desktop: two columns — media/stats/description left, price/actions/history right */
          <div className="grid grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-6">
            <div className="space-y-4">
              {media}
              {statTiles}
              {descriptionCard}
              {landlordCard}
            </div>
            <div className="flex flex-col space-y-4">
              {priceBlock}
              {actions}
              {historyCard}
              {notesCard}
              <div className="pt-2">{footerRow}</div>
            </div>
          </div>
        ) : (
          /* Mobile: single column */
          <div className="space-y-4">
            {media}
            {priceBlock}
            {statTiles}
            {actions}
            {descriptionCard}
            {landlordCard}
            {historyCard}
            {notesCard}
            {footerRow}
          </div>
        )}
        {property.url && (
          <a
            href={property.url}
            target="_blank"
            rel="noopener noreferrer"
            className="sm:hidden mt-4 flex items-center justify-center gap-1.5 h-11 rounded-xl text-sm font-medium text-accent border border-line hover:bg-accent-soft"
          >
            Open listing <ExternalLink className="w-4 h-4" />
          </a>
        )}
      </Sheet>

      <DeleteConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          onDelete(property.id);
          onClose();
        }}
        property={property}
      />
    </>
  );
};

export default PropertyDetail;
